import type {
  AdminAddPayload,
  AdminPayload,
  ApiResponse,
  DetailsApiResponse,
  TableFilterParams,
  UpdateProfileImagePayload,
  UserInterface,
  UserPassword,
} from "interface/admin";
import type { StatusInterface } from "interface/common";
import { type RootState, store } from "../store";
import * as API from "@utils/apiPath";
import { baseApi } from "./baseApi";

const getFormattedParams = (params: TableFilterParams) => {
  const normalizeFilterValue = (
    value?: string | string[] | null
  ): string => {
    if (Array.isArray(value)) {
      return value[0] ?? "";
    }
    return value ?? "";
  };
  const normalizeStatusValue = (
    value?: Array<string | boolean> | string | boolean | null
  ): string => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item)).join("-");
    }
    return value !== null && value !== undefined ? String(value) : "";
  };
  return {
    firstName: normalizeFilterValue(params?.firstName),
    email: normalizeFilterValue(params?.email),
    mobile_number: normalizeFilterValue(params?.mobile_number),
    status: normalizeStatusValue(
      params?.status as Array<string | boolean> | string | boolean | null
    ),
    updatedAt: normalizeFilterValue(params?.updatedAt),
    createdAt: normalizeFilterValue(params?.createdAt),
    sort: params?.sortField ?? "",
    direction: params?.sortOrder === "ascend" ? "asc" : "desc",
    page: params?.page ?? 1,
    limit: params?.pageSize ?? 10,
  };
};

export const adminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdmins: builder.query<ApiResponse, { [key: string]: string | number }>({
      query: (params) => ({
        url: API.GET_ADMIN,
        method: "GET",
        params,
      }),
      providesTags: ["Admin"],
    }),
    getAdminDetails: builder.query<DetailsApiResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/${API.GET_ADMIN}/details/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { id }) => [{ type: "Admin", id }],
    }),
    createAdmin: builder.mutation<ApiResponse, AdminAddPayload>({
      query: ({ params }) => ({
        url: API.GET_ADMIN,
        method: "POST",
        body: params,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
      invalidatesTags: ["Admin"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          arg.onClose();
        } catch (error) {
          console.error("Failed to create admin:", error);
        }
      },
    }),
    updateAdmin: builder.mutation<ApiResponse, AdminPayload>({
      query: ({ params }) => ({
        url: API.GET_ADMIN,
        method: "PUT",
        body: params,
      }),
      invalidatesTags: ["Admin"],
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
          const state = getState() as unknown as RootState;
          const adminFilter = state.saveFilter?.filters?.adminFilter;
          if (adminFilter) {
            dispatch(
              adminApi.endpoints.getAdmins.initiate(
                getFormattedParams(adminFilter)
              )
            );
          }
          arg.onClose();
        } catch (error) {
          console.error("Failed to update admin:", error);
        }
      },
    }),
    updateAdminStatus: builder.mutation<ApiResponse, StatusInterface>({
      query: ({ id, ...rest }) => ({
        url: `/${API.GET_ADMIN}/status/${id}`,
        method: "PUT",
        body: rest,
      }),
      invalidatesTags: ["Admin"],
      async onQueryStarted(_arg, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
          const state = getState() as unknown as RootState;
          const adminFilter = state.saveFilter?.filters?.adminFilter;
          if (adminFilter) {
            dispatch(
              adminApi.endpoints.getAdmins.initiate(
                getFormattedParams(adminFilter)
              )
            );
          }
        } catch (error) {
          console.error("Failed to update admin status:", error);
        }
      },
    }),
    deleteAdmin: builder.mutation<ApiResponse, StatusInterface>({
      query: ({ id }) => ({
        url: `/${API.GET_ADMIN}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Admin"],
      async onQueryStarted(_arg, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
          const state = getState() as unknown as RootState;
          const adminFilter = state.saveFilter?.filters?.adminFilter;
          if (adminFilter) {
            dispatch(
              adminApi.endpoints.getAdmins.initiate(
                getFormattedParams(adminFilter)
              )
            );
          }
        } catch (error) {
          console.error("Failed to delete admin:", error);
        }
      },
    }),
    updateProfileImage: builder.mutation<
      ApiResponse,
      UpdateProfileImagePayload
    >({
      query: ({ formData, id }) => {
        const data = new FormData();
        if (id) {
          data.append("id", id);
        }
        if (formData.has("image")) {
          data.append("image", formData.get("image") as File);
        }
        return {
          url: `${API.GET_ADMIN}/profile-image`,
          method: "POST",
          body: data,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        };
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Admin", id },
        "Admin",
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (arg.id) {
            dispatch(
              adminApi.endpoints.getAdminDetails.initiate({ id: arg.id })
            );
          }
          arg.closeModal();
        } catch (error) {
          console.error("Failed to update profile image:", error);
        }
      },
    }),
    updateProfile: builder.mutation<ApiResponse, UserInterface>({
      query: ({ params }) => ({
        url: `${API.GET_ADMIN}/update`,
        method: "PUT",
        body: params,
      }),
      invalidatesTags: (_result, _error, { params }) => [
        { type: "Admin", id: params.id },
        "Admin",
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (arg.params.id) {
            dispatch(
              adminApi.endpoints.getAdminDetails.initiate({ id: arg.params.id })
            );
          }
          arg.onClose();
        } catch (error) {
          console.error("Failed to update profile:", error);
        }
      },
    }),
    updatePassword: builder.mutation<ApiResponse, UserPassword>({
      query: ({ params }) => ({
        url: `${API.GET_ADMIN}/update-password`,
        method: "POST",
        body: params,
      }),
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          console.error("Failed to update password:", error);
        }
      },
    }),
    downloadReport: builder.mutation<ApiResponse, void>({
      query: () => {
        const adminFilter = store.getState().saveFilter.filters?.adminFilter;
        const query = getFormattedParams(adminFilter || {});
        return {
          url: `${API.GET_ADMIN}/download`,
          method: "GET",
          params: query,
        };
      },
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          const fileName = result.data.data.fileUrl;
          if (fileName) {
            window.open(fileName, "_blank");
          }
        } catch (error) {
          console.error("Failed to download report:", error);
        }
      },
    }),
    bulkUpdateAdminStatus: builder.mutation<
      ApiResponse,
      { ids: string[]; status: boolean }
    >({
      query: ({ ids, status }) => ({
        url: `/${API.GET_ADMIN}/bulk-status`,
        method: "PUT",
        body: { ids, status },
      }),
      invalidatesTags: ["Admin"],
    }),
    bulkDeleteAdmins: builder.mutation<ApiResponse, { ids: string[] }>({
      query: ({ ids }) => ({
        url: `/${API.GET_ADMIN}/bulk-delete`,
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["Admin"],
    }),
  }),
});

export const {
  useGetAdminsQuery,
  useGetAdminDetailsQuery,
  useCreateAdminMutation,
  useUpdateAdminMutation,
  useUpdateAdminStatusMutation,
  useDeleteAdminMutation,
  useUpdateProfileImageMutation,
  useUpdateProfileMutation,
  useUpdatePasswordMutation,
  useDownloadReportMutation,
  useBulkUpdateAdminStatusMutation,
  useBulkDeleteAdminsMutation,
} = adminApi;
